/**
 * The code a client pastes to add a lead through the public API (docs/38): SCRAM-SHA-256 for a
 * token, then one POST. Plain Node 18+ with no packages, because Zapier's Code step allows none
 * and a client's developer should be able to read every line of what holds their secret.
 *
 * Written with string concatenation, not template literals, so this file does not have to escape
 * the snippet's own. Kept in step with apps/api/src/modules/publicapi/scram.ts (clientFinish).
 */

const TOKEN_FN = [
  "const crypto = require('crypto');",
  '',
  '// SCRAM-SHA-256 (RFC 7804): proves the secret without sending it, and checks the',
  '// platform proves itself back. A token lasts 15 minutes.',
  'async function arcToken(base, clientId, secret) {',
  "  const H = (b) => crypto.createHash('sha256').update(b).digest();",
  "  const mac = (k, s) => crypto.createHmac('sha256', k).update(s).digest();",
  "  const b64 = (s) => Buffer.from(s).toString('base64');",
  "  const data = (h) => Buffer.from((/data=([^,\\s]+)/.exec(h || '') || [])[1] || '', 'base64').toString();",
  "  const nonce = crypto.randomBytes(18).toString('base64');",
  "  const bare = 'n=' + clientId + ',r=' + nonce;",
  "  let res = await fetch(base + '/public/token', { method: 'POST',",
  "    headers: { Authorization: 'SCRAM-SHA-256 data=' + b64('n,,' + bare) } });",
  "  const www = res.headers.get('www-authenticate') || '';",
  "  const sid = (/sid=([^,\\s]+)/.exec(www) || [])[1];",
  '  const first = data(www);',
  "  const a = Object.fromEntries(first.split(',').map((p) => [p[0], p.slice(2)]));",
  "  if (!sid || !a.r || !a.r.startsWith(nonce)) throw new Error('Sign-in refused (' + res.status + ')');",
  "  const salted = crypto.pbkdf2Sync(secret, Buffer.from(a.s, 'base64'), Number(a.i), 32, 'sha256');",
  "  const clientKey = mac(salted, 'Client Key');",
  "  const noProof = 'c=biws,r=' + a.r;",
  "  const auth = bare + ',' + first + ',' + noProof;",
  '  const sig = mac(H(clientKey), auth);',
  "  const proof = Buffer.from(clientKey.map((x, i) => x ^ sig[i])).toString('base64');",
  "  res = await fetch(base + '/public/token', { method: 'POST',",
  "    headers: { Authorization: 'SCRAM-SHA-256 sid=' + sid + ', data=' + b64(noProof + ',p=' + proof) } });",
  "  if (!res.ok) throw new Error('Sign-in refused (' + res.status + ')');",
  "  const expect = 'v=' + mac(mac(salted, 'Server Key'), auth).toString('base64');",
  "  if (data(res.headers.get('authentication-info')) !== expect) throw new Error('Not the ArcNet platform');",
  '  return (await res.json()).access_token;',
  '}',
];

const POST_FN = [
  '',
  'async function arcAddLead(base, token, lead) {',
  "  const res = await fetch(base + '/leads', { method: 'POST',",
  "    headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },",
  '    body: JSON.stringify(lead) });',
  '  const body = await res.json();',
  "  if (!res.ok) throw new Error('Lead refused (' + res.status + '): ' + body.error);",
  "  return body; // { ok: true, status: 'added' | 'received', ignored_fields? }",
  '}',
];

export function leadSnippet(how: 'node' | 'zapier', o: { base: string; clientId: string; fields: readonly string[] }): string {
  const q = (s: string) => JSON.stringify(s);
  const extra = o.fields.map((k) => (how === 'zapier' ? '  ' + k + ': inputData.' + k + ',' : '  ' + k + ": '...',"));
  if (how === 'zapier') {
    return [
      ...TOKEN_FN, ...POST_FN, '',
      'const base = ' + q(o.base) + ';',
      'const token = await arcToken(base, ' + q(o.clientId) + ', inputData.secret);',
      'output = await arcAddLead(base, token, {',
      '  phone: inputData.phone,',
      '  name: inputData.name,',
      '  email: inputData.email,',
      ...extra,
      '});',
    ].join('\n');
  }
  return [
    ...TOKEN_FN, ...POST_FN, '',
    '// Keep the secret out of the code: an environment variable, a vault.',
    'const base = ' + q(o.base) + ';',
    'const clientId = ' + q(o.clientId) + ';',
    '',
    '(async () => {',
    '  const token = await arcToken(base, clientId, process.env.ARC_CLIENT_SECRET);',
    '  // Reuse the token for 15 minutes, then sign in again.',
    '  console.log(await arcAddLead(base, token, {',
    "    phone: '082 123 4567',",
    "    name: 'Thandi Mokoena',",
    "    email: 'thandi@example.co.za',",
    ...extra.map((l) => '  ' + l),
    "    page: 'https://your-site.co.za/quote',   // where they asked to be called",
    "    consent_ip: '198.51.100.7',              // their address, as your site saw it",
    "    user_agent: 'Mozilla/5.0 ...',           // their browser",
    '  }));',
    '})();',
  ].join('\n');
}
